import React, { useState, useRef, useEffect, useCallback } from 'react'
import Modal from '../../../Modal'
import styles from './index.module.scss'
import { WorkspaceService, UserService } from '../../../../services'
import CloseIcon from '../../../../assets/icon/CloseIcon'
import CheckboxActiveIcon from '../../../../assets/icon/CheckboxActiveIcon'
import CheckboxIcon from '../../../../assets/icon/CheckboxIcon'
import {type Workspace, useWorkspace} from "../../../../context/workspace.context";

interface UserOption {
    id: string
    email: string
    first_name: string
    last_name: string
    avatar?: string
}

interface WorkspaceModalProps {
    onClose: () => void
    onCreated?: (workspace: Workspace | null) => void
}

export default function WorkspaceModal({ onClose, onCreated }: WorkspaceModalProps) {
    const [name, setName] = useState('')
    const [search, setSearch] = useState('')
    const [searchFocused, setSearchFocused] = useState(false)
    const [selected, setSelected] = useState<UserOption[]>([])
    const [users, setUsers] = useState<UserOption[]>([])
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [usersLoaded, setUsersLoaded] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const searchRef = useRef<HTMLDivElement>(null)
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const {fetchWorkspaces} = useWorkspace()

    const isValid = name.trim() !== ''

    const filteredUsers = users.filter((user) => {
        if (!search) return true
        const displayName = getUserDisplayName(user).toLowerCase()
        const normalizedQuery = search.toLowerCase()

        return displayName.includes(normalizedQuery) || user.email?.toLowerCase().includes(normalizedQuery)
    })

    const showDropdown = searchFocused && (filteredUsers.length > 0 || loadingUsers)

    function getUserDisplayName(user: UserOption) {
        if (user.first_name || user.last_name) {
            return `${user.first_name || ''} ${user.last_name || ''}`.trim()
        }

        return user.email
    }

    function getUserInitials(user: UserOption) {
        const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()

        return initials || user.email?.[0]?.toUpperCase() || 'U'
    }

    const fetchUsers = useCallback(async (searchQuery: string) => {
        setLoadingUsers(true)

        try {
            const data = await UserService.getUsers({
                search: searchQuery || undefined,
                limit: 1000,
            })
            const list = Array.isArray(data) ? data : []

            setUsers(list)
            setUsersLoaded(true)
        } catch (e) {
            console.error('Failed to fetch users:', e)
            setUsers([])
        } finally {
            setLoadingUsers(false)
        }
    }, [])

    const handleSearchChange = (value: string) => {
        setSearch(value)

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current)
        }

        searchTimerRef.current = setTimeout(() => {
            fetchUsers(value)
        }, 300)
    }

    const toggleUser = (user: UserOption) => {
        setSelected((prev) => {
            const isSelected = prev.find((selectedUser) => selectedUser.id === user.id)

            if (isSelected) {
                return prev.filter((selectedUser) => selectedUser.id !== user.id)
            }

            return [...prev, user]
        })
    }

    const handleRemove = (id: string) => {
        setSelected((prev) => prev.filter((selectedUser) => selectedUser.id !== id))
    }

    const handleCreate = async () => {
        if (!isValid || submitting) return

        setSubmitting(true)
        setError(null)

        try {
            const createdWorkspace = await WorkspaceService.createWorkspace({
                name: name.trim(),
                initial_members: selected.map((selectedUser) => ({ user_id: selectedUser.id })),
            })
            await fetchWorkspaces()
            onCreated?.(createdWorkspace?.workspace ?? createdWorkspace ?? null)
            onClose()

        } catch (e: any) {
            console.error('Failed to create workspace:', e)
            setError(e?.data?.detail || 'Ошибка при создании пространства')
        } finally {
            setSubmitting(false)
        }
    }

    const handleSearchFocus = () => {
        setSearchFocused(true)

        if (!usersLoaded) {
            fetchUsers('')
        }
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setSearchFocused(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)

            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current)
            }
        }
    }, [])

    return (
        <Modal
            isOpen={true}
            onRequestClose={onClose}
            width={446}
        >
            <div className={styles.workspaceModal}>
                <div className={styles.workspaceModal__close} onClick={onClose}>
                    <CloseIcon />
                </div>

                <div className={styles.workspaceModal__content}>
                    <div className={styles.workspaceModal__title}>
                        Новое пространство
                    </div>

                    <div className={styles.workspaceModal__field}>
                        <div className={styles.workspaceModal__label}>
                            Название рабочего пространства
                        </div>
                        <input
                            className={styles.workspaceModal__input}
                            placeholder="Введите название"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>

                    <div className={`${styles.workspaceModal__field} ${selected.length > 0 ? styles.workspaceModal__fieldSearch : ''}`}>
                        <div className={styles.workspaceModal__label}>
                            Имена, фамилии, адреса электронной почты или группы
                        </div>
                        <div
                            ref={searchRef}
                            className={`${styles.workspaceModal__search} ${searchFocused ? styles.workspaceModal__searchFocused : ''}`}
                        >
                            <svg className={styles.workspaceModal__searchIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M4.99902 11.2922C4.99902 7.8171 7.81612 5 11.2912 5C14.7662 5 17.5833 7.8171 17.5833 11.2922C17.5833 12.7779 17.0685 14.1431 16.2075 15.2196L16.1635 15.2745L18.8148 17.9259C19.0605 18.1716 19.0605 18.57 18.8148 18.8157C18.5691 19.0614 18.1707 19.0614 17.925 18.8157L15.2736 16.1644L15.2187 16.2084C14.1422 17.0695 12.7769 17.5843 11.2912 17.5843C7.81612 17.5843 4.99902 14.7672 4.99902 11.2922ZM11.2912 6.25843C8.51113 6.25843 6.25746 8.51211 6.25746 11.2922C6.25746 14.0722 8.51113 16.3259 11.2912 16.3259C14.0712 16.3259 16.3249 14.0722 16.3249 11.2922C16.3249 8.51211 14.0712 6.25843 11.2912 6.25843Z" fill="currentColor" />
                            </svg>
                            <input
                                className={styles.workspaceModal__searchInput}
                                placeholder="Имена, фамилии, адреса электронной почты или группы"
                                value={search}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onFocus={handleSearchFocus}
                            />
                            {showDropdown && (
                                <div className={styles.workspaceModal__dropdown}>
                                    {loadingUsers ? (
                                        <div className={styles.workspaceModal__dropdownItem} style={{ justifyContent: 'center', color: '#B1AEC7', cursor: 'default' }}>
                                            Загрузка...
                                        </div>
                                    ) : filteredUsers.map((user) => (
                                        <div
                                            key={user.id}
                                            className={styles.workspaceModal__dropdownItem}
                                            onClick={() => toggleUser(user)}
                                        >
                                            <div className={styles.workspaceModal__dropdownItemLeft}>
                                                {user.avatar ? (
                                                    <img
                                                        className={styles.workspaceModal__dropdownAvatar}
                                                        src={user.avatar}
                                                        alt={getUserDisplayName(user)}
                                                    />
                                                ) : (
                                                    <div className={styles.workspaceModal__dropdownAvatarPlaceholder}>
                                                        {getUserInitials(user)}
                                                    </div>
                                                )}
                                                <span className={styles.workspaceModal__dropdownName}>{getUserDisplayName(user)}</span>
                                            </div>
                                            {selected.find((selectedUser) => selectedUser.id === user.id)
                                                ? <CheckboxActiveIcon />
                                                : <CheckboxIcon />
                                            }
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {selected.length > 0 && (
                        <div className={styles.workspaceModal__chips}>
                            {selected.map((item) => (
                                <div
                                    key={item.id}
                                    className={`${styles.workspaceModal__chip} ${styles.workspaceModal__chipDefault}`}
                                >
                                    {item.avatar ? (
                                        <img
                                            className={styles.workspaceModal__chipAvatar}
                                            src={item.avatar}
                                            alt={getUserDisplayName(item)}
                                        />
                                    ) : (
                                        <div className={styles.workspaceModal__chipAvatarPlaceholder}>
                                            {getUserInitials(item)}
                                        </div>
                                    )}
                                    <span>{getUserDisplayName(item)}</span>
                                    <svg
                                        className={styles.workspaceModal__chipRemove}
                                        onClick={() => handleRemove(item.id)}
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M12 4L4 12" stroke="#202026" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M4 4L12 12" stroke="#202026" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            ))}
                        </div>
                    )}

                    {error && (
                        <div className={styles.workspaceModal__error}>
                            {error}
                        </div>
                    )}

                    <button
                        className={`${styles.workspaceModal__submit} ${(!isValid || submitting) ? styles.workspaceModal__submitDisabled : ''}`}
                        disabled={!isValid || submitting}
                        onClick={handleCreate}
                    >
                        {submitting ? 'Создание...' : 'Создать пространство'}
                    </button>
                    <button className={styles.workspaceModal__cancel} onClick={onClose}>
                        Отменить создание
                    </button>
                </div>
            </div>
        </Modal>
    )
}
